/**
 * Templates — 템플릿 관리 페이지
 *
 * 템플릿 목록 + ZIP 업로드로 새 템플릿 생성
 */

import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useApiFetch } from '../hooks/useApiFetch'
import { useToast } from '../components/ToastContainer'
import { apiFetch } from '../lib/api'
import { copySmartstoreLinkToClipboard } from '../lib/smartstoreLink'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Spinner } from '../ui/Spinner'
import { Modal } from '../ui/Modal'
import { Drawer } from '../ui/Drawer'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import { TemplateImportV2Modal } from './TemplateImportV2Modal'

interface ConversionRecord {
  id: string
  templateId: string
  templateName: string | null
  templateSlug: string | null
  model: string
  inputTokens: number
  outputTokens: number
  detectedSections: Array<{ sectionType: string; confidence: string; bindings: string[] }>
  notes: string[]
  createdAt: string
  importedVia?: string | null
  sourceZipS3Key?: string | null
}

interface UploadResult {
  template: Template
  extractedAssets: Array<{ originalPath: string; s3Url: string; category: string }>
  fontFaces: Array<{ family: string; src: string }>
}
import styles from './Templates.module.css'

// ── 타입 ──

interface Template {
  id: string
  name: string
  slug: string
  category: string
  tier: string
  description: string | null
  thumbnailUrl: string | null
  isActive: boolean
  cachedHtml: string | null
  createdAt: string
  updatedAt: string
  hasShell?: boolean
}

interface TemplatesResponse {
  templates: Template[]
}

function fileNameToTemplateName(fileName: string): string {
  return fileName.replace(/\.(zip|html|htm)$/i, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface ZipAnalysis {
  totalFiles: number
  totalSize: number
  htmlFiles: string[]
  cssFiles: string[]
  fontFiles: string[]
  imageFiles: string[]
  otherFiles: string[]
}

async function analyzeFile(file: File): Promise<ZipAnalysis | null> {
  if (!file.name.endsWith('.zip')) return null

  const buffer = await file.arrayBuffer()
  const view = new DataView(buffer)
  const entries: { name: string; size: number }[] = []

  let eocdOffset = -1
  for (let i = buffer.byteLength - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === 0x06054b50) { eocdOffset = i; break }
  }
  if (eocdOffset === -1) return null

  const cdOffset = view.getUint32(eocdOffset + 16, true)
  const cdEntries = view.getUint16(eocdOffset + 10, true)

  let pos = cdOffset
  for (let i = 0; i < cdEntries && pos < buffer.byteLength; i++) {
    if (view.getUint32(pos, true) !== 0x02014b50) break
    const nameLen = view.getUint16(pos + 28, true)
    const extraLen = view.getUint16(pos + 30, true)
    const commentLen = view.getUint16(pos + 32, true)
    const uncompSize = view.getUint32(pos + 24, true)
    const nameBytes = new Uint8Array(buffer, pos + 46, nameLen)
    const name = new TextDecoder().decode(nameBytes)
    if (!name.endsWith('/')) entries.push({ name, size: uncompSize })
    pos += 46 + nameLen + extraLen + commentLen
  }

  const ext = (n: string) => n.split('.').pop()?.toLowerCase() ?? ''
  const filtered = entries.filter((e) => !e.name.startsWith('__MACOSX') && !e.name.startsWith('.'))

  return {
    totalFiles: filtered.length,
    totalSize: filtered.reduce((s, e) => s + e.size, 0),
    htmlFiles: filtered.filter((e) => ['html', 'htm'].includes(ext(e.name))).map((e) => e.name),
    cssFiles: filtered.filter((e) => ext(e.name) === 'css').map((e) => e.name),
    fontFiles: filtered.filter((e) => ['woff2', 'woff', 'ttf', 'otf'].includes(ext(e.name))).map((e) => e.name),
    imageFiles: filtered.filter((e) => ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'].includes(ext(e.name))).map((e) => e.name),
    otherFiles: filtered.filter((e) => !['html', 'htm', 'css', 'woff2', 'woff', 'ttf', 'otf', 'png', 'jpg', 'jpeg', 'webp', 'svg', 'gif', 'js'].includes(ext(e.name))).map((e) => e.name),
  }
}

interface DropPreview {
  file: File
  name: string
  slug: string
  analysis: ZipAnalysis | null
}

const CATEGORY_KR: Record<string, string> = {
  CLASSIC: '클래식', MODERN: '모던', VINTAGE: '빈티지',
  FLORAL: '플로럴', MINIMAL: '미니멀', ELEGANT: '엘레건트', NATURAL: '내추럴',
}

function toSlug(name: string): string {
  return name.toLowerCase()
    .replace(/[^a-z0-9가-힣\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 64)
}

export function Templates() {
  const navigate = useNavigate()
  const { data, isLoading, refetch } = useApiFetch<TemplatesResponse>('/api/admin/templates')
  const templates = data?.templates ?? []
  const { addToast } = useToast()

  // 변환 이력 Drawer
  const [showHistory, setShowHistory] = useState(false)
  const { data: historyData, refetch: refetchHistory } = useApiFetch<{ conversions: ConversionRecord[] }>(
    showHistory ? '/api/admin/templates/conversions' : null,
  )
  const conversions = historyData?.conversions ?? []

  // 재변환 (retry): 해당 conversion 의 원본 ZIP 으로 최신 파이프라인 재실행.
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const handleRetry = useCallback(
    async (conv: ConversionRecord, refetchHistory: () => void) => {
      if (!conv.sourceZipS3Key) return
      setRetryingId(conv.id)
      addToast({ type: 'info', message: `재변환 시작 (${conv.templateSlug ?? conv.templateId})` })
      try {
        const res = await fetch(
          `/api/admin/templates/${conv.templateId}/imports/${conv.id}/retry`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ skipLlm: true }),
          },
        )
        if (!res.ok || !res.body) {
          addToast({ type: 'error', message: `재변환 실패 (${res.status})` })
          return
        }
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let finalStatus: string | null = null
        let finalError: string | null = null
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const evt = JSON.parse(line.slice(6)) as
                | { type: 'log'; message: string }
                | { type: 'done'; status: 'SUCCESS' | 'FAILED'; errors?: Array<{ message: string }> }
                | { type: 'error'; error: string }
              if (evt.type === 'done') {
                finalStatus = evt.status
                if (evt.status === 'FAILED' && evt.errors?.length) {
                  finalError = evt.errors.map((e) => e.message).join(' / ')
                }
              } else if (evt.type === 'error') {
                finalError = evt.error
              }
            } catch {
              // ignore malformed line
            }
          }
        }
        if (finalStatus === 'SUCCESS') {
          addToast({ type: 'success', message: '재변환 성공 — liquidHtml 갱신됨' })
          refetch()
          refetchHistory()
        } else {
          addToast({ type: 'error', message: finalError ?? '재변환 실패' })
        }
      } catch (err: any) {
        addToast({ type: 'error', message: err?.message || '재변환 네트워크 오류' })
      } finally {
        setRetryingId(null)
      }
    },
    [addToast, refetch],
  )

  // 삭제 확인
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null)
  const [deleting, setDeleting] = useState(false)

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await apiFetch(`/api/admin/templates/${deleteTarget.id}`, { method: 'DELETE' })
      addToast({ type: 'success', message: `"${deleteTarget.name}" 삭제 완료` })
      setDeleteTarget(null)
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '삭제 실패' })
    } finally {
      setDeleting(false)
    }
  }, [deleteTarget, addToast, refetch])

  // 드래그앤드롭
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounter = useRef(0)

  // 미리보기 모달
  const [dropPreview, setDropPreview] = useState<DropPreview | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')
  const [v2Mode, setV2Mode] = useState<null | 'auto' | 'stepwise'>(null)

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current++
    if (dragCounter.current === 1) setIsDragOver(true)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current--
    if (dragCounter.current === 0) setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current = 0
    setIsDragOver(false)
    if (uploading) return

    const file = e.dataTransfer.files[0]
    if (!file) return

    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!ext || !['zip', 'html', 'htm'].includes(ext)) {
      addToast({ type: 'error', message: 'ZIP 또는 HTML 파일만 업로드할 수 있습니다' })
      return
    }

    const name = fileNameToTemplateName(file.name)
    if (!name) {
      addToast({ type: 'error', message: '파일 이름에서 템플릿 이름을 생성할 수 없습니다' })
      return
    }

    const analysis = await analyzeFile(file)
    setDropPreview({ file, name, slug: toSlug(name), analysis })
  }, [uploading, addToast])

  const handleConfirmUpload = useCallback(async () => {
    if (!dropPreview) return
    const { file, name, slug } = dropPreview

    setUploading(true)
    setUploadProgress('Presigned URL 발급 중...')

    try {
      const presign = await apiFetch<{ key: string; uploadUrl: string }>('/api/admin/templates/presign', {
        method: 'POST',
        body: JSON.stringify({ fileName: file.name, contentType: file.type || 'application/octet-stream' }),
      })

      setUploadProgress(`S3 업로드 중... (${formatSize(file.size)})`)
      const s3Res = await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      })
      if (!s3Res.ok) throw new Error(`S3 업로드 실패 (${s3Res.status})`)

      setUploadProgress('서버 처리 중...')
      const result = await apiFetch<UploadResult>('/api/admin/templates/process-s3', {
        method: 'POST',
        body: JSON.stringify({
          s3Key: presign.key,
          name,
          slug,
          category: 'CLASSIC',
          fileName: file.name,
        }),
      })

      const assetCount = result.extractedAssets?.length ?? 0
      const msg = assetCount > 0
        ? `템플릿 "${result.template.name}" 생성 완료 (에셋 ${assetCount}개 업로드)`
        : `템플릿 "${result.template.name}" 생성 완료`
      addToast({ type: 'success', message: msg })
      setDropPreview(null)
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '업로드 실패' })
    } finally {
      setUploading(false)
      setUploadProgress('')
    }
  }, [dropPreview, addToast, refetch, navigate])

  // 스마트스토어 nid.naver.com 진입 링크 생성 후 클립보드 복사 (개별 템플릿)
  const copyTemplateSmartstoreLink = useCallback(async (templateId: string) => {
    try {
      await copySmartstoreLinkToClipboard('template', templateId)
      addToast({ type: 'success', message: '스토어 링크 복사됨' })
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : '스토어 링크 생성 실패',
      })
    }
  }, [addToast])

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.title}>템플릿</h2>
        <div className={styles.headerActions}>
          <Button variant="primary" onClick={() => setV2Mode('auto')}>ZIP 가져오기 (자동)</Button>
          <Button variant="primary" onClick={() => setV2Mode('stepwise')}>단계별 변환</Button>
          <Button variant="outline" onClick={() => setShowHistory(true)}>변환 이력</Button>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loading}><Spinner /></div>
      ) : (
        <>
          <div
            className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className={styles.dropZoneIcon}>&#8682;</div>
            <p className={styles.dropZoneTitle}>ZIP 또는 HTML 파일을 여기에 드래그하세요</p>
            <p className={styles.dropZoneDesc}>파일 이름이 템플릿 이름으로 사용됩니다</p>
          </div>
          {templates.length > 0 && (
            <div className={styles.grid}>
              {templates.map((t) => (
                <div
                  key={t.id}
                  className={styles.card}
                  onClick={() => navigate(`/wedding/templates/${t.id}`)}
                >
                  <div className={styles.cardPreview}>
                    {t.thumbnailUrl ? (
                      <img src={t.thumbnailUrl} alt={t.name} />
                    ) : t.cachedHtml ? (
                      <iframe
                        srcDoc={t.cachedHtml}
                        title={t.name}
                        className={styles.cardIframe}
                      />
                    ) : (
                      <div className={styles.cardPlaceholder}>{t.name}</div>
                    )}
                  </div>
                  <div className={styles.cardInfo}>
                    <div className={styles.cardNameRow}>
                      <span className={styles.cardName}>{t.name}</span>
                      <button
                        type="button"
                        className={styles.cardLinkBtn}
                        onClick={(e) => { e.stopPropagation(); copyTemplateSmartstoreLink(t.id) }}
                        title="스마트스토어 진입 링크 복사"
                      >
                        스토어 링크
                      </button>
                      <button
                        type="button"
                        className={styles.cardLinkBtn}
                        disabled={!t.hasShell}
                        onClick={(e) => { e.stopPropagation(); navigate(`/wedding/templates/${t.id}/compose`) }}
                        title={t.hasShell ? '섹션 조립' : '시드 필요 — compositionShell 없음'}
                      >
                        조립
                      </button>
                      <button
                        type="button"
                        className={styles.cardDeleteBtn}
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(t) }}
                        title="삭제"
                      >
                        ×
                      </button>
                    </div>
                    <div className={styles.cardMeta}>
                      <Badge color={t.isActive ? 'green' : 'yellow'}>
                        {t.isActive ? '활성' : '비활성'}
                      </Badge>
                      <span className={styles.cardCategory}>{CATEGORY_KR[t.category] ?? t.category}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* 드롭 파일 미리보기 모달 */}
      <Modal
        open={dropPreview !== null}
        onClose={() => { if (!uploading) setDropPreview(null) }}
        title={uploading ? '업로드 중...' : '템플릿 업로드 확인'}
      >
        {dropPreview && !uploading && (
          <div className={styles.previewModal}>
            <div className={styles.previewInfo}>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>파일</span>
                <span className={styles.previewValue}>{dropPreview.file.name} ({formatSize(dropPreview.file.size)})</span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>템플릿 이름</span>
                <span className={styles.previewValue}>{dropPreview.name}</span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>Slug</span>
                <span className={styles.previewValue}>{dropPreview.slug}</span>
              </div>
            </div>
            {dropPreview.analysis && (
              <div className={styles.previewAnalysis}>
                <div className={styles.previewAnalysisHeader}>
                  ZIP 구성 — {dropPreview.analysis.totalFiles}개 파일 ({formatSize(dropPreview.analysis.totalSize)})
                </div>
                <div className={styles.previewAnalysisBody}>
                  {dropPreview.analysis.htmlFiles.length > 0 && (
                    <div className={styles.previewGroup}>
                      <Badge color="blue">HTML ({dropPreview.analysis.htmlFiles.length})</Badge>
                      {dropPreview.analysis.htmlFiles.map((f) => <span key={f} className={styles.previewFile}>{f}</span>)}
                    </div>
                  )}
                  {dropPreview.analysis.cssFiles.length > 0 && (
                    <div className={styles.previewGroup}>
                      <Badge color="green">CSS ({dropPreview.analysis.cssFiles.length})</Badge>
                      {dropPreview.analysis.cssFiles.map((f) => <span key={f} className={styles.previewFile}>{f}</span>)}
                    </div>
                  )}
                  {dropPreview.analysis.fontFiles.length > 0 && (
                    <div className={styles.previewGroup}>
                      <Badge color="yellow">폰트 ({dropPreview.analysis.fontFiles.length})</Badge>
                      {dropPreview.analysis.fontFiles.map((f) => <span key={f} className={styles.previewFile}>{f}</span>)}
                    </div>
                  )}
                  {dropPreview.analysis.imageFiles.length > 0 && (
                    <div className={styles.previewGroup}>
                      <Badge color="green">이미지 ({dropPreview.analysis.imageFiles.length})</Badge>
                      {dropPreview.analysis.imageFiles.length <= 10
                        ? dropPreview.analysis.imageFiles.map((f) => <span key={f} className={styles.previewFile}>{f}</span>)
                        : <span className={styles.previewFile}>{dropPreview.analysis.imageFiles.slice(0, 5).join(', ')} 외 {dropPreview.analysis.imageFiles.length - 5}개</span>
                      }
                    </div>
                  )}
                  {dropPreview.analysis.otherFiles.length > 0 && (
                    <div className={styles.previewGroup}>
                      <Badge color="red">기타 ({dropPreview.analysis.otherFiles.length})</Badge>
                      {dropPreview.analysis.otherFiles.map((f) => <span key={f} className={styles.previewFile}>{f}</span>)}
                    </div>
                  )}
                </div>
              </div>
            )}
            {!dropPreview.analysis && (
              <div className={styles.previewAnalysis}>
                <div className={styles.previewAnalysisHeader}>
                  HTML 파일 — {formatSize(dropPreview.file.size)}
                </div>
              </div>
            )}
            <div className={styles.previewActions}>
              <Button variant="outline" onClick={() => setDropPreview(null)}>취소</Button>
              <Button onClick={handleConfirmUpload}>업로드</Button>
            </div>
          </div>
        )}
        {dropPreview && uploading && (
          <div className={styles.uploadStatus}>
            <Spinner />
            <span>{uploadProgress}</span>
          </div>
        )}
      </Modal>

      {/* 변환 이력 Drawer */}
      <Drawer
        open={showHistory}
        onClose={() => setShowHistory(false)}
        title="AI 변환 이력"
        size="lg"
      >
        {conversions.length === 0 ? (
          <div className={styles.historyEmpty}>변환 이력이 없습니다</div>
        ) : (
          <div className={styles.historyList}>
            {conversions.map((conv) => (
              <div key={conv.id} className={styles.historyItem}>
                <div className={styles.historyHeader}>
                  <span className={styles.historyTemplate}>
                    {conv.templateName ?? conv.templateSlug ?? conv.templateId}
                  </span>
                  <span className={styles.historyDate}>
                    {new Date(conv.createdAt).toLocaleString('ko-KR')}
                  </span>
                </div>
                <div className={styles.historyMeta}>
                  <Badge color="blue">{conv.model.split('-').slice(0, 2).join('-')}</Badge>
                  <span className={styles.historyTokens}>
                    {(conv.inputTokens + conv.outputTokens).toLocaleString()} tokens
                  </span>
                </div>
                {conv.detectedSections && conv.detectedSections.length > 0 && (
                  <div className={styles.historySections}>
                    {conv.detectedSections.map((s) => (
                      <Badge
                        key={s.sectionType}
                        color={s.confidence === 'high' ? 'green' : s.confidence === 'medium' ? 'yellow' : 'red'}
                      >
                        {s.sectionType}
                      </Badge>
                    ))}
                  </div>
                )}
                {conv.notes && conv.notes.length > 0 && (
                  <div className={styles.historyNotes}>
                    {conv.notes.map((n, i) => <p key={i}>{n}</p>)}
                  </div>
                )}
                <div className={styles.historyActions}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setShowHistory(false); navigate(`/wedding/templates/${conv.templateId}`) }}
                  >
                    템플릿 편집
                  </Button>
                  {conv.importedVia === 'zip-v2' && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!conv.sourceZipS3Key || retryingId !== null}
                      onClick={() => handleRetry(conv, refetchHistory)}
                      title={!conv.sourceZipS3Key ? '원본 ZIP 없음 — 재변환 불가' : '최신 파이프라인으로 재변환'}
                    >
                      {retryingId === conv.id ? (<><Spinner size="sm" /> 재변환 중...</>) : '재변환'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Drawer>

      {/* 삭제 확인 */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="템플릿 삭제"
        message={`"${deleteTarget?.name}" 템플릿을 삭제하시겠습니까? 변환 이력은 보존됩니다.`}
        confirmLabel="삭제"
        variant="danger"
        loading={deleting}
      />

      {/* ZIP v2 가져오기 */}
      <TemplateImportV2Modal
        open={v2Mode !== null}
        initialMode={v2Mode ?? 'auto'}
        onClose={() => setV2Mode(null)}
        onSuccess={() => refetch()}
      />
    </div>
  )
}
